import React, { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "../ui/dialog";

export interface DimensionValue {
  key: string;
  name: string;
}

export interface Dimension {
  title: string;
  // HubSpot internal field name for this dimension (e.g. "country", "product_category")
  // Persisted next to the title and values when serialized
  fieldName?: string;
  values: DimensionValue[];
}

export interface RevenueMatrixData {
  dimension1: Dimension;
  dimension2: Dimension;
  values: Record<string, Record<string, number>>; // [dimension1Key][dimension2Key] = value
}

export interface HubSpotFormField {
  name: string;
  label: string;
  fieldType: string;
  description?: string;
  required?: boolean;
  displayOrder?: number;
  hidden?: boolean;
  enabled?: boolean;
  defaultValue?: string;
  placeholder?: string;
  selectedOptions?: any[];
  options?: HubSpotFormFieldOption[];
}

export interface HubSpotFormFieldOption {
  label: string;
  value: string;
  displayOrder?: number;
  hidden?: boolean;
  description?: string;
  readOnly?: boolean;
  doubleData?: number;
}

export interface FormRevenueMatrix {
  formId: string;
  formName: string;
  dimension1FieldName?: string;
  dimension2FieldName?: string;
  matrixData: RevenueMatrixData;
}

export interface CampaignRevenueMatrices {
  matrices: FormRevenueMatrix[];
}

export interface RevenueMatrixProps {
  data: RevenueMatrixData;
  onChange: (data: RevenueMatrixData) => void;
  disabled?: boolean;
}

export interface FormRevenueMatrixProps {
  formMatrix: FormRevenueMatrix;
  formFields: HubSpotFormField[];
  onChange: (updatedMatrix: FormRevenueMatrix) => void;
  onRemove: () => void;
  disabled?: boolean;
}

export interface MultiFormRevenueMatrixProps {
  selectedFormIds: Set<string>;
  formsData: Array<{ id: string; name: string }>;
  campaignMatrices: CampaignRevenueMatrices;
  onChange: (matrices: CampaignRevenueMatrices) => void;
  onLoadFormFields: (formId: string) => Promise<HubSpotFormField[]>;
  disabled?: boolean;
}

// Default dummy data
const createDefaultData = (): RevenueMatrixData => ({
  dimension1: {
    title: "Dimension 1",
    fieldName: undefined,
    values: []
  },
  dimension2: {
    title: "Dimension 2", 
    fieldName: undefined,
    values: []
  },
  values: {}
});

const createMatrixFromFields = (
  field1: HubSpotFormField | null, 
  field2: HubSpotFormField | null
): RevenueMatrixData => {
  return {
    dimension1: {
      title: field1?.label || "Dimension 1",
      fieldName: field1?.name,
      values: field1?.options?.map(opt => ({
        key: opt.value,
        name: opt.label
      })) || []
    },
    dimension2: {
      title: field2?.label || "Dimension 2",
      fieldName: field2?.name,
      values: field2?.options?.map(opt => ({
        key: opt.value,
        name: opt.label
      })) || []
    },
    values: {}
  };
};

// Matrix column width configuration
const MAX_COLUMN_WIDTH_PX = 100;
const COLUMN_STYLE: React.CSSProperties = {
  width: MAX_COLUMN_WIDTH_PX,
  maxWidth: MAX_COLUMN_WIDTH_PX,
  minWidth: MAX_COLUMN_WIDTH_PX,
};

const RevenueMatrix: React.FC<RevenueMatrixProps> = ({ data, onChange, disabled = false }) => {
  const handleValueChange = (dim1Key: string, dim2Key: string, value: string) => {
    const numericValue = parseInt(value) || 0;
    
    const newValues = { ...data.values };
    if (!newValues[dim1Key]) {
      newValues[dim1Key] = {};
    }
    newValues[dim1Key][dim2Key] = numericValue;
    
    onChange({
      ...data,
      values: newValues
    });
  };

  const getValue = (dim1Key: string, dim2Key: string): number => {
    return data.values[dim1Key]?.[dim2Key] || 0;
  };

  // Determine if we need scrolling based on dimensions
  const needsVerticalScroll = data.dimension1.values.length > 10;
  const needsHorizontalScroll = data.dimension2.values.length > 5;

  if (data.dimension1.values.length === 0 || data.dimension2.values.length === 0) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center text-gray-500">
            <p>Please select fields for both dimensions to create the revenue matrix.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Calculate minimum table width based on number of columns
  const minTableWidth = (data.dimension2.values.length + 1) * MAX_COLUMN_WIDTH_PX;

  return (
    <Card>
      <CardContent className="p-4">
        <div className={`overflow-auto ${needsHorizontalScroll ? 'max-w-full' : ''} ${needsVerticalScroll ? 'max-h-96' : ''}`}>
          <table 
            className="table-fixed border-collapse border border-gray-300"
            style={{ 
              width: `${minTableWidth}px`,
              minWidth: `${minTableWidth}px`
            }}
          >
            <thead className="sticky top-0 z-10">
              <tr>
                <th
                  className={`border border-gray-300 bg-gray-50 p-3 text-left font-medium sticky left-0 z-20`}
                  title={`${data.dimension1.title} / ${data.dimension2.title}`}
                  style={COLUMN_STYLE}
                >
                  <div className="truncate">{data.dimension1.title} / {data.dimension2.title}</div>
                </th>
                {data.dimension2.values.map((dim2Value) => (
                  <th
                    key={dim2Value.key}
                    className={`border border-gray-300 bg-gray-50 p-3 text-center font-medium`}
                    title={dim2Value.name}
                    style={COLUMN_STYLE}
                  >
                    <div className="truncate">{dim2Value.name}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.dimension1.values.map((dim1Value) => (
                <tr key={dim1Value.key}>
                  <td
                    className={`border border-gray-300 bg-gray-50 p-3 font-medium sticky left-0 z-10 whitespace-nowrap`}
                    title={dim1Value.name}
                    style={COLUMN_STYLE}
                  >
                    <div className="truncate">{dim1Value.name}</div>
                  </td>
                  {data.dimension2.values.map((dim2Value) => (
                    <td key={dim2Value.key} className={`border border-gray-300 p-2 align-middle`} style={COLUMN_STYLE}>
                      <Input
                        type="number"
                        value={getValue(dim1Value.key, dim2Value.key) || ''}
                        onChange={(e) => handleValueChange(dim1Value.key, dim2Value.key, e.target.value)}
                        placeholder="0"
                        className="w-full text-center"
                        style={{ fontSize: '14px', padding: '4px 8px' }}
                        disabled={disabled}
                        min="0"
                        title={`${data.dimension1.title}: ${dim1Value.name} | ${data.dimension2.title}: ${dim2Value.name}`}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {(needsVerticalScroll || needsHorizontalScroll) && (
          <div className="mt-2 text-xs text-gray-500">
            {needsVerticalScroll && needsHorizontalScroll && "Scroll horizontally and vertically to view all data"}
            {needsVerticalScroll && !needsHorizontalScroll && "Scroll vertically to view all rows"}
            {!needsVerticalScroll && needsHorizontalScroll && "Scroll horizontally to view all columns"}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const FormRevenueMatrixComponent: React.FC<FormRevenueMatrixProps> = ({
  formMatrix,
  formFields,
  onChange,
  onRemove,
  disabled = false
}) => {
  const [loading, setLoading] = useState(false);
  const [isFullScreenOpen, setIsFullScreenOpen] = useState(false);

  const selectionFields = formFields.filter(field => 
    field.options && field.options.length > 0
  );

  const handleFieldSelection = async (fieldName: string, dimension: 'dimension1' | 'dimension2') => {
    const field = selectionFields.find(f => f.name === fieldName);
    const otherDimension = dimension === 'dimension1' ? 'dimension2' : 'dimension1';
    const otherFieldName = dimension === 'dimension1' ? formMatrix.dimension2FieldName : formMatrix.dimension1FieldName;
    const otherField = otherFieldName ? selectionFields.find(f => f.name === otherFieldName) : null;

    const updatedMatrix: FormRevenueMatrix = {
      ...formMatrix,
      [dimension === 'dimension1' ? 'dimension1FieldName' : 'dimension2FieldName']: fieldName,
      matrixData: createMatrixFromFields(
        dimension === 'dimension1' ? (field || null) : (otherField || null),
        dimension === 'dimension2' ? (field || null) : (otherField || null)
      )
    };

    onChange(updatedMatrix);
  };

  return (
    <Card className="mb-4">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">Revenue Matrix: {formMatrix.formName}</CardTitle>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsFullScreenOpen(true)}
              className="hover:bg-accent"
            >
              Full Screen
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onRemove}
              disabled={disabled}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              Remove
            </Button>
          </div>
        </div>
        <p className="text-sm text-gray-600">
          Select fields from the HubSpot form to define the matrix dimensions
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Field Selection */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Horizontal Dimension (First Field)</Label>
            <Select
              value={formMatrix.dimension1FieldName || ""}
              onValueChange={(value) => handleFieldSelection(value as string, 'dimension1')}
              disabled={disabled || loading}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select field for horizontal axis" />
              </SelectTrigger>
              <SelectContent>
                {selectionFields.map((field) => (
                  <SelectItem key={field.name} value={field.name}>
                    {field.label} ({field.options?.length || 0} options)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Vertical Dimension (Second Field)</Label>
            <Select
              value={formMatrix.dimension2FieldName || ""}
              onValueChange={(value) => handleFieldSelection(value as string, 'dimension2')}
              disabled={disabled || loading}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select field for vertical axis" />
              </SelectTrigger>
              <SelectContent>
                {selectionFields.map((field) => (
                  <SelectItem key={field.name} value={field.name}>
                    {field.label} ({field.options?.length || 0} options)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Revenue Matrix */}
        <RevenueMatrix
          data={formMatrix.matrixData}
          onChange={(matrixData) => onChange({ ...formMatrix, matrixData })}
          disabled={disabled}
        />
      </CardContent>

      {/* Full Screen Modal */}
      <Dialog open={isFullScreenOpen} onOpenChange={setIsFullScreenOpen}>
        <DialogContent className="bg-background h-[800px] w-[1200px] max-w-none rounded-lg p-0 !w-[1200px]" style={{ width: '1200px', maxWidth: '1200px' }}>
          <div className="h-full w-full flex flex-col overflow-hidden">
            <div className="flex items-center justify-between border-b px-4 py-3 bg-background flex-shrink-0">
              <div className="min-w-0">
                <DialogTitle className="truncate">Revenue Matrix: {formMatrix.formName}</DialogTitle>
                <DialogDescription className="mt-1">Edit the revenue matrix. Changes save automatically.</DialogDescription>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setIsFullScreenOpen(false)}>
                  Close
                </Button>
              </div>
            </div>
            <div className="flex-1 overflow-auto p-4">
              <div className="h-full w-full">
                <RevenueMatrix
                  data={formMatrix.matrixData}
                  onChange={(matrixData) => onChange({ ...formMatrix, matrixData })}
                  disabled={disabled}
                />
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

const MultiFormRevenueMatrix: React.FC<MultiFormRevenueMatrixProps> = ({
  selectedFormIds,
  formsData,
  campaignMatrices,
  onChange,
  onLoadFormFields,
  disabled = false
}) => {
  const [formFields, setFormFields] = useState<Record<string, HubSpotFormField[]>>({});
  const [loadingFormFields, setLoadingFormFields] = useState<Record<string, boolean>>({});

  const loadFields = async (formId: string) => {
    if (formFields[formId] || loadingFormFields[formId]) return;

    setLoadingFormFields(prev => ({ ...prev, [formId]: true }));
    try {
      const fields = await onLoadFormFields(formId);
      setFormFields(prev => ({ ...prev, [formId]: fields }));
    } catch (error) {
      console.error(`Failed to load fields for form ${formId}:`, error);
    } finally {
      setLoadingFormFields(prev => ({ ...prev, [formId]: false }));
    }
  };

  // Sync matrices with selected forms
  React.useEffect(() => {
    const selectedFormsArray: string[] = Array.from<string>(selectedFormIds);
    const currentMatrices = campaignMatrices.matrices;

    // Remove matrices for unselected forms
    const filteredMatrices = currentMatrices.filter(matrix => 
      selectedFormIds.has(matrix.formId)
    );

    // Add matrices for newly selected forms
    const newMatrices = selectedFormsArray
      .filter(formId => !currentMatrices.some(matrix => matrix.formId === formId))
      .map(formId => {
        const form = formsData.find(f => f.id === formId);
        return {
          formId,
          formName: form?.name || formId,
          matrixData: createDefaultData()
        } as FormRevenueMatrix;
      });

    const updatedMatrices = [...filteredMatrices, ...newMatrices];

    if (updatedMatrices.length !== currentMatrices.length || 
        !updatedMatrices.every(matrix => 
          currentMatrices.some(existing => 
            existing.formId === matrix.formId && 
            existing.formName === matrix.formName
          )
        )) {
      onChange({ matrices: updatedMatrices });
    }

    // Load form fields for selected forms
    selectedFormsArray.forEach((formId: string) => {
      loadFields(formId);
    });
  }, [selectedFormIds, formsData]);

  const handleMatrixChange = (formId: string, updatedMatrix: FormRevenueMatrix) => {
    const updatedMatrices = campaignMatrices.matrices.map(matrix =>
      matrix.formId === formId ? updatedMatrix : matrix
    );
    onChange({ matrices: updatedMatrices });
  };

  const handleMatrixRemove = (formId: string) => {
    const updatedMatrices = campaignMatrices.matrices.filter(matrix => 
      matrix.formId !== formId
    );
    onChange({ matrices: updatedMatrices });
  };

  if (campaignMatrices.matrices.length === 0) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center text-gray-500">
            <p>Select HubSpot forms above to create revenue matrices.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label className="text-base font-medium">Revenue Matrices by HubSpot Form</Label>
        <span className="text-sm text-gray-600">
          {campaignMatrices.matrices.length} matrix{campaignMatrices.matrices.length !== 1 ? 'es' : ''}
        </span>
      </div>
      
      {campaignMatrices.matrices.map((matrix) => (
        <FormRevenueMatrixComponent
          key={matrix.formId}
          formMatrix={matrix}
          formFields={formFields[matrix.formId] || []}
          onChange={(updatedMatrix) => handleMatrixChange(matrix.formId, updatedMatrix)}
          onRemove={() => handleMatrixRemove(matrix.formId)}
          disabled={disabled || loadingFormFields[matrix.formId]}
        />
      ))}
    </div>
  );
};

// Utility functions for serialization/deserialization
export const serializeRevenueMatrix = (data: RevenueMatrixData): string => {
  return JSON.stringify(data);
};

export const deserializeRevenueMatrix = (json: string): RevenueMatrixData => {
  try {
    if (!json || json.trim() === '') {
      return createDefaultData();
    }
    const parsed = JSON.parse(json);
    
    // Validate the structure
    if (!parsed.dimension1 || !parsed.dimension2 || !parsed.values) {
      return createDefaultData();
    }

    // Backward compatibility: ensure fieldName exists on both dimensions
    const ensureFieldName = (dim: any): Dimension => ({
      title: dim.title || 'Dimension',
      fieldName: dim.fieldName, // may be undefined in older data
      values: Array.isArray(dim.values) ? dim.values : []
    });

    return {
      dimension1: ensureFieldName(parsed.dimension1),
      dimension2: ensureFieldName(parsed.dimension2),
      values: parsed.values as Record<string, Record<string, number>>
    } as RevenueMatrixData;
  } catch (error) {
    console.error('Error deserializing revenue matrix:', error);
    return createDefaultData();
  }
};

export const serializeCampaignMatrices = (matrices: CampaignRevenueMatrices): string => {
  return JSON.stringify(matrices);
};

export const deserializeCampaignMatrices = (json: string): CampaignRevenueMatrices => {
  try {
    if (!json || json.trim() === '') {
      return { matrices: [] };
    }
    const parsed = JSON.parse(json);
    
    // Validate the structure
    if (!parsed.matrices || !Array.isArray(parsed.matrices)) {
      return { matrices: [] };
    }

    // Normalize each matrix's data to ensure fieldName is present (back-compat)
    const normalized = (parsed.matrices as any[]).map(m => {
      const matrixDataStr = JSON.stringify(m.matrixData || {});
      const matrixData = deserializeRevenueMatrix(matrixDataStr);
      return {
        ...m,
        matrixData
      } as FormRevenueMatrix;
    });
    
    return { matrices: normalized } as CampaignRevenueMatrices;
  } catch (error) {
    console.error('Error deserializing campaign matrices:', error);
    return { matrices: [] };
  }
};

export { createDefaultData, MultiFormRevenueMatrix };
export default RevenueMatrix;